import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import { useTour } from '../tour/TourContext';
import { WELCOME, TOUR_STEPS } from '../tour/copy';

// Owns the driver.js guided tour. Rendered by HomePage so its step callbacks
// can drive HomePage state (set material, select a yard for the selling map,
// pick a site for the cheapest view) and make the walkthrough "live".
export default function HomeTour({
  setMaterial,
  onSelectYard,
  onSelectSite,
  resetMap,
  firstYardId,
  firstSiteId,
}: {
  setMaterial: (m: string) => void;
  onSelectYard: (yardId: string) => void;
  onSelectSite: (id: string | null) => void;
  resetMap: () => void;
  firstYardId: string | null;
  firstSiteId: string | null;
}) {
  const { tourRequested, consumeTourRequest } = useTour();
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  // Run a step's live side-effect just before it is highlighted.
  const applyStepEffect = (selector?: string) => {
    switch (selector) {
      case 'material-filter':
        setMaterial('hardwood_mulch');
        break;
      case 'home-map':
        if (firstYardId) onSelectYard(firstYardId); // -> selling mode
        break;
      case 'cheapest-panel':
        if (firstSiteId) onSelectSite(firstSiteId); // -> cheapest mode
        break;
      default:
        break;
    }
  };

  const startTour = () => {
    if (driverRef.current) return; // guard against double-start (StrictMode)

    const steps: any[] = [];

    // Step 0 — Welcome (no element => driver centers it like a modal).
    steps.push({
      popover: { title: WELCOME.title, description: WELCOME.body },
    });

    for (const s of TOUR_STEPS) {
      // Skip a step whose target isn't currently in the DOM (e.g. AI bubble
      // when the chat panel happens to be open).
      if (s.selector && !document.querySelector(`[data-tour="${s.selector}"]`)) continue;
      steps.push({
        element: s.selector ? `[data-tour="${s.selector}"]` : undefined,
        popover: {
          title: s.title,
          description: s.body,
          side: s.side,
          align: s.align,
        },
        onHighlightStarted: () => {
          applyStepEffect(s.selector);
          // state change re-renders / moves elements — re-measure the spotlight
          requestAnimationFrame(() => driverRef.current?.refresh());
        },
      });
    }

    const d = driver({
      showProgress: true,
      allowClose: true,
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Done',
      steps,
      onDestroyed: () => {
        driverRef.current = null;
        resetMap(); // don't leave the map in selling/cheapest mode after the tour
      },
    });
    driverRef.current = d;
    d.drive();
  };

  // Launch when the nav button requests a tour.
  useEffect(() => {
    if (!tourRequested) return;
    consumeTourRequest();
    startTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRequested]);

  // Tear down if the user navigates away mid-tour.
  useEffect(
    () => () => {
      driverRef.current?.destroy();
      driverRef.current = null;
    },
    []
  );

  return null;
}
